/**
 * Editor state.
 *
 * Undo history stores whole-document snapshots rather than inverse operations:
 * a seating plan is small (tens of tables) and snapshots make every action
 * trivially reversible, including compound ones like "delete table with guests".
 * The camera is deliberately excluded — undoing a chair move should not also
 * teleport the view.
 */

import { create } from 'zustand'

import {
  type Bounds,
  MIN_SEATS,
  ROOM_BOUNDS,
  clampSeatCount,
  clampTableCenter,
  maxSeatCount,
  rotatedHalfExtent,
  tableBounds,
  unionBounds,
} from '../lib/geometry'
import {
  type AlignEdge,
  type Boxed,
  type DistributeAxis,
  type Move,
  alignBoxes,
  distributeBoxes,
} from '../lib/arrange'
import { separateFrom } from '../lib/collision'
import { LIMITS } from '../lib/config'
import { newId } from '../lib/ids'
import { shareUnchanged } from '../lib/share'
import { type PlanDoc, docOf, docsEqual, mergeDocs } from '../lib/merge'
import { localTableName, nextTableName, planWords } from '../lib/planWords'
import type {
  DetectedTable,
  EventKind,
  Guest,
  Plan,
  Rsvp,
  Seat,
  SeatLabelMode,
  Table,
  TableColor,
  TableShape,
  Viewport,
} from '../lib/types'

export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'conflict'

/**
 * The parts of a plan that undo/redo covers — the same unit the merge works on,
 * which is what lets another tab's change be folded into the history as well as into
 * the live document. (The settings are in here so the settings modal is
 * undoable like any other edit; the camera deliberately is not.)
 */
type Snapshot = PlanDoc

export interface SeatRef {
  tableId: string
  seatId: string
}

interface PlanState {
  plan: Plan | null
  past: Snapshot[]
  future: Snapshot[]
  /**
   * The last document storage acknowledged — the merge base. Set on load, on
   * every save ack (to exactly what we sent, not to the response, since the
   * local document may have moved on) and after every integration.
   */
  baseDoc: PlanDoc | null
  selectedTableIds: string[]
  activeSeat: SeatRef | null
  saveStatus: SaveStatus
  saveError: string | null
  toast: { id: number; message: string; tone: 'info' | 'warn' | 'success' } | null

  // lifecycle
  loadPlan: (plan: Plan) => void
  applySavedPlan: (plan: Plan, sentDoc?: PlanDoc) => void
  /** Fold another tab's save into this editor. See the action for the rules. */
  integrateStoredPlan: (stored: Plan) => void
  setSaveStatus: (status: SaveStatus, error?: string | null) => void
  showToast: (message: string, tone?: 'info' | 'warn' | 'success') => void
  dismissToast: () => void

  // history
  beginTransaction: () => void
  /**
   * Run several store actions as ONE undo step: snapshot once, then let the
   * inner actions mutate without pushing snapshots of their own. Without this,
   * "swap two guests" or "recolour five tables" lands as a pile of entries and
   * one ⌘Z restores a half-state nobody ever saw.
   */
  transaction: (fn: () => void) => void
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean

  // selection
  selectTables: (ids: string[]) => void
  toggleTableSelection: (id: string) => void
  clearSelection: () => void
  setActiveSeat: (ref: SeatRef | null) => void

  // plan meta
  renamePlan: (title: string) => void
  setPlanDetails: (patch: {
    eventKind?: EventKind
    eventDate?: string | null
    venue?: string | null
  }) => void
  setViewport: (viewport: Viewport) => void

  // plan settings — what a NEW table is born with, and how chairs print
  setDefaultSeatCount: (shape: TableShape, count: number) => void
  setDefaultTableColor: (color: TableColor) => void
  setSeatLabels: (mode: SeatLabelMode) => void
  /** Push the stored default onto every existing table of that shape. */
  applyDefaultSeatCount: (shape: TableShape) => { tables: number; unseated: number }
  applyDefaultTableColor: () => number

  // tables
  addTable: (shape: TableShape, at?: { x: number; y: number }) => string
  deleteTables: (ids: string[]) => void
  duplicateTable: (id: string) => string | null
  nudgeTables: (ids: string[], dx: number, dy: number, options?: { transient?: boolean }) => void
  /**
   * One drag frame for a whole selection: every table to its absolute target,
   * confined against a floor that excludes the *entire* moving set. Setting
   * them one by one would let two co-moving tables outside the hall extend the
   * floor for each other and ratchet the pair outward without limit.
   */
  moveTables: (targets: Array<{ id: string; x: number; y: number }>) => void
  alignTables: (ids: string[], edge: AlignEdge) => void
  distributeTables: (ids: string[], along: DistributeAxis) => void
  setSeatCountFor: (ids: string[], count: number) => { tables: number; unseated: number }
  setRotationFor: (ids: string[], rotation: number, options?: { transient?: boolean }) => void
  /** Settle a just-released gesture: nudge the moved tables off anything they
   * were dropped on. Joins the gesture's transaction (no history entry). */
  settleTables: (ids: string[]) => void
  setTablePosition: (id: string, x: number, y: number) => void
  setTableRotation: (id: string, rotation: number) => void
  renameTable: (id: string, name: string) => void
  setTableColor: (id: string, color: TableColor) => void
  bringToFront: (id: string) => void
  importDetectedTables: (tables: DetectedTable[], mode: 'replace' | 'add') => number

  // chairs
  addSeat: (tableId: string) => boolean
  removeSeat: (tableId: string, seatId?: string) => boolean
  setSeatCount: (tableId: string, count: number) => void

  // guests
  addGuest: (fullName: string, patch?: Partial<Omit<Guest, 'id'>>) => string
  updateGuest: (id: string, patch: Partial<Omit<Guest, 'id'>>) => void
  deleteGuest: (id: string) => void
  importGuests: (raw: string) => number
  assignGuest: (guestId: string, ref: SeatRef) => void
  /**
   * Put a guest in a chair the way dropping them on it does: whoever sits there
   * swaps into the guest's old chair, or returns to the roster when the guest
   * had none. One undo entry; their own chair is no edit at all.
   */
  seatGuest: (guestId: string, target: SeatRef) => void
  /** Seat a guest at a table's first free chair — a tap on the table itself. Null, and no edit, when it is full. */
  seatGuestAtTable: (guestId: string, tableId: string) => SeatRef | null
  clearSeat: (ref: SeatRef) => void
  unseatTables: (ids: string[]) => number
  /**
   * Trades the guests of two tables — or moves everyone across, when one is
   * empty. Null when there is no such pair; counts say who moved which way and
   * how many no chair could be found for.
   */
  swapTableGuests: (aId: string, bId: string) => TableSwap | null
  seatNewGuest: (ref: SeatRef, fullName: string) => string
  autoSeat: () => number
}

const HISTORY_LIMIT = 80

const clone = <T,>(value: T): T =>
  typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value))

const snapshotOf = (plan: Plan): Snapshot => docOf(plan)

function makeSeats(count: number): Seat[] {
  return Array.from({ length: count }, (_, position) => ({
    id: newId(),
    position,
    guestId: null,
    label: null,
  }))
}

export interface TableSwap {
  /** Guests who went from the first table to the second. */
  toB: number
  /** Guests who went from the second table to the first. */
  toA: number
  /** Guests beyond the receiving shape's cap, back on the list. */
  unseated: number
}

/**
 * Seats `incoming` at a table already cleared of its own guests.
 *
 * Everyone keeps their chair number where the table has it; the rest take the
 * first free chairs. The table grows to seat them all, up to its shape's cap,
 * and never shrinks — removing chairs is its own decision. Returns how many
 * could not be seated.
 */
function seatArrivals(table: Table, incoming: Seat[]): number {
  const target = Math.min(maxSeatCount(table.shape), Math.max(table.seats.length, incoming.length))
  if (target > table.seats.length) resizeSeats(table, target)
  const waiting: Seat[] = []
  for (const arrival of incoming) {
    const chair = table.seats[arrival.position]
    if (chair && !chair.guestId) chair.guestId = arrival.guestId
    else waiting.push(arrival)
  }
  let unseated = 0
  for (const arrival of waiting) {
    const free = table.seats.find((seat) => !seat.guestId)
    if (free) free.guestId = arrival.guestId
    else unseated += 1
  }
  return unseated
}

/** Positions are always 0..n-1 in array order — `validateDoc` rejects holes. */
function reindex(seats: Seat[]): Seat[] {
  return seats.map((seat, position) => (seat.position === position ? seat : { ...seat, position }))
}

/**
 * Grow or shrink a table's chairs to `target`, in place.
 *
 * Shrinking drops trailing *empty* chairs before occupied ones, so losing a
 * chair never unseats someone while an empty one is still available. Returns
 * how many guests it had to unseat anyway.
 */
function resizeSeats(table: Table, target: number): number {
  let unseated = 0
  while (table.seats.length < target) {
    table.seats.push({ id: newId(), position: table.seats.length, guestId: null, label: null })
  }
  while (table.seats.length > target) {
    const lastEmpty = [...table.seats].reverse().find((seat) => !seat.guestId)
    const drop = lastEmpty ?? table.seats[table.seats.length - 1]
    if (drop.guestId) unseated += 1
    table.seats = table.seats.filter((seat) => seat.id !== drop.id)
  }
  table.seats = reindex(table.seats)
  table.seatCount = table.seats.length
  return unseated
}

/**
 * The floor the plan may use: the standard hall, extended to take in any
 * furniture already beyond it.
 *
 * The hall alone cannot hold every real venue — an imported room at its
 * tightest collision-free density can still be taller than 2970 — and a floor
 * plan is a drawing of a building, so the building wins: the white floor grows
 * to hold the furniture rather than leaving tables on the void. It never
 * shrinks below the hall itself, so an ordinary plan sees the same fixed room
 * as ever. `exclude` leaves out the tables being moved — otherwise a dragged
 * table would carry its own allowance outward and escape without limit.
 */
export function floorBounds(tables: Table[], exclude?: Set<string>): Bounds {
  const boxes = tables
    .filter((table) => !exclude?.has(table.id))
    .map((table) => tableBounds(table.shape, table.seatCount, table.x, table.y, table.rotation))
  const floor = unionBounds([ROOM_BOUNDS, ...boxes]) ?? ROOM_BOUNDS
  // …never past where a backup can carry a table, or a table moved up against
  // one that stands there would make the next backup unrestorable.
  const limit = LIMITS.maxCoordinate
  return {
    minX: Math.max(floor.minX, -limit),
    minY: Math.max(floor.minY, -limit),
    maxX: Math.min(floor.maxX, limit),
    maxY: Math.min(floor.maxY, limit),
  }
}

/**
 * The top of the stacking order, with room for `adding` more above it.
 *
 * Each new or raised table goes one higher, without end; a backup refuses a
 * stack past `maxZIndex`. Should the next ones not fit — a plan that came
 * from a file already near the top — the stack is renumbered 1..n first, in
 * the order it is drawn, which nobody can see.
 */
function topZ(plan: Plan, adding = 1): number {
  const maxZ = plan.tables.reduce((max, t) => Math.max(max, t.zIndex), 0)
  if (maxZ + adding <= LIMITS.maxZIndex) return maxZ
  plan.tables
    .map((table, order) => ({ table, order }))
    .sort((a, b) => a.table.zIndex - b.table.zIndex || a.order - b.order)
    .forEach(({ table }, rank) => {
      table.zIndex = rank + 1
    })
  return plan.tables.length
}

/**
 * Clamp one axis, honouring a table that is already outside the floor.
 *
 * A plan saved before the room existed may hold tables beyond even the
 * extended floor. Teleporting someone's stored layout the moment they nudge
 * something would be worse than the unbounded canvas ever was, so a table that
 * starts outside is allowed to stay outside — it just may not go *further* out.
 */
function confineAxis(current: number, next: number, low: number, high: number): number {
  if (next >= low && next <= high) return next
  if (current < low && next < low) return Math.max(next, current)
  if (current > high && next > high) return Math.min(next, current)
  return Math.min(Math.max(next, low), high)
}

/** Where a table may actually go, chairs included. */
function confine(table: Table, x: number, y: number, bounds: Bounds): { x: number; y: number } {
  const half = rotatedHalfExtent(table.shape, table.seatCount, table.rotation)
  return {
    x: confineAxis(table.x, x, bounds.minX + half.x, bounds.maxX - half.x),
    y: confineAxis(table.y, y, bounds.minY + half.y, bounds.maxY - half.y),
  }
}

/**
 * How many guests `applyDefaultSeatCount` would evict — asked *before* the
 * change, so the modal can warn rather than apologise.
 */
export function guestsUnseatedByDefault(plan: Plan | null, shape: TableShape): number {
  if (!plan) return 0
  const target = clampSeatCount(plan.settings.defaultSeats[shape], shape)
  let total = 0
  for (const table of plan.tables) {
    if (table.shape !== shape) continue
    const occupied = table.seats.filter((seat) => seat.guestId).length
    if (target < occupied) total += occupied - target
  }
  return total
}

/**
 * How many guests one shared chair count would evict from a selection — asked
 * *before* the change so the panel can warn rather than apologise, the same way
 * `guestsUnseatedByDefault` serves the settings modal.
 *
 * The clamp is per shape and happens here too, or the warning would lie: asking
 * for 20 across a round table and a banquet table only shortens the round one
 * to 13, and a count that ignored that would promise evictions that never come.
 */
export function guestsUnseatedBySeatCount(
  plan: Plan | null,
  ids: string[],
  count: number,
): number {
  if (!plan) return 0
  const chosen = new Set(ids)
  let total = 0
  for (const table of plan.tables) {
    if (!chosen.has(table.id)) continue
    const target = clampSeatCount(count, table.shape)
    const occupied = table.seats.filter((seat) => seat.guestId).length
    if (target < occupied) total += occupied - target
  }
  return total
}

export const usePlanStore = create<PlanState>()((set, get) => {
  /** True while `transaction` runs, so inner actions skip their own snapshot. */
  let inTransaction = false

  /** Snapshot the current document onto the undo stack. Call BEFORE mutating. */
  function pushUndo(): void {
    if (inTransaction) return
    const { plan, past } = get()
    if (!plan) return
    const next = [...past, snapshotOf(plan)]
    if (next.length > HISTORY_LIMIT) next.shift()
    set({ past: next, future: [] })
  }

  /** Mutate the plan and mark it dirty. `history: false` for drag frames. */
  function edit(mutate: (plan: Plan) => Plan | void, options: { history?: boolean } = {}): void {
    const { plan } = get()
    if (!plan) return
    if (options.history !== false) pushUndo()
    const draft = clone(plan)
    const result = mutate(draft) ?? draft
    set({ plan: shareUnchanged(plan, result), saveStatus: 'dirty', saveError: null })
  }

  /**
   * The per-frame edits — dragging and turning — change a table or two, but
   * `edit()` deep-copies the whole plan: a thousand guests, sixty times a second
   * on a big wedding. These rebuild only the tables that actually change. No
   * history entry, like `edit(…, { history: false })`, whose semantics they keep:
   * the plan is marked dirty either way.
   */
  function patchTables(
    patch: (table: Table) => Partial<Pick<Table, 'x' | 'y' | 'rotation'>> | null,
  ): void {
    const { plan } = get()
    if (!plan) return
    let changed = false
    const tables = plan.tables.map((table) => {
      const next = patch(table)
      if (!next) return table
      const moved = { ...table, ...next }
      if (moved.x === table.x && moved.y === table.y && moved.rotation === table.rotation) return table
      changed = true
      return moved
    })
    set(changed ? { plan: { ...plan, tables }, saveStatus: 'dirty', saveError: null } : { saveStatus: 'dirty', saveError: null })
  }

  /**
   * Shared body of `alignTables` / `distributeTables`: tables in, boxes out to
   * `lib/arrange`, moves back in. One `edit()` for the whole selection, and
   * nothing at all when the arrangement asks for no movement — a second press
   * of "align left" on an already-flush row must not cost an undo entry.
   */
  function arrangeSelection(ids: string[], arrange: (boxes: Boxed[]) => Move[]): void {
    const current = get().plan
    if (!current) return
    const chosen = new Set(ids)
    const boxes: Boxed[] = current.tables
      .filter((table) => chosen.has(table.id))
      .map((table) => {
        const half = rotatedHalfExtent(table.shape, table.seatCount, table.rotation)
        return { id: table.id, x: table.x, y: table.y, halfX: half.x, halfY: half.y }
      })

    const moves = arrange(boxes)
    if (moves.length === 0) return

    edit((plan) => {
      const bounds = floorBounds(plan.tables, chosen)
      for (const move of moves) {
        const table = plan.tables.find((t) => t.id === move.id)
        if (!table) continue
        const at = confine(table, move.x, move.y, bounds)
        table.x = at.x
        table.y = at.y
      }
    })
  }

  function restore(snapshot: Snapshot, plan: Plan): Plan {
    return {
      ...plan,
      title: snapshot.title,
      eventKind: snapshot.eventKind,
      eventDate: snapshot.eventDate,
      venue: snapshot.venue,
      settings: clone(snapshot.settings),
      tables: clone(snapshot.tables),
      guests: clone(snapshot.guests),
    }
  }

  return {
    plan: null,
    past: [],
    future: [],
    baseDoc: null,
    selectedTableIds: [],
    activeSeat: null,
    saveStatus: 'idle',
    saveError: null,
    toast: null,

    // --- lifecycle ---------------------------------------------------------

    loadPlan: (plan) =>
      set({
        plan,
        past: [],
        future: [],
        baseDoc: docOf(plan),
        selectedTableIds: [],
        activeSeat: null,
        saveStatus: 'idle',
        saveError: null,
      }),

    /**
     * Fold a save's result back in. Only the version and derived fields are
     * taken: the local document may already have moved on while the write was
     * in flight, and clobbering it would undo whatever the user did meanwhile.
     *
     * `sentDoc` is what we actually wrote, and becomes the new merge base — the
     * base has to be what storage *acknowledged*, which is the payload, not the
     * response and not the live document.
     */
    applySavedPlan: (saved, sentDoc) => {
      const { plan } = get()
      if (!plan) {
        set({ plan: saved, baseDoc: docOf(saved), saveStatus: 'saved' })
        return
      }
      const photoById = new Map(saved.guests.map((g) => [g.id, g.photoUrl]))
      set({
        plan: {
          ...plan,
          version: saved.version,
          updatedAt: saved.updatedAt,
          seatCount: saved.seatCount,
          seatedCount: saved.seatedCount,
          // Photo URLs are minted when a plan is read, so adopt them where we lack one.
          guests: plan.guests.map((guest) =>
            guest.photoUrl ? guest : { ...guest, photoUrl: photoById.get(guest.id) ?? null },
          ),
        },
        baseDoc: sentDoc ?? get().baseDoc,
      })
    },

    /**
     * Fold another tab's save into this editor.
     *
     * base = the last acknowledged doc, ours = the live document, theirs = what
     * storage now holds. History is rebased through the same merge, so undoing
     * your own edit never silently destroys or resurrects work done in the other
     * tab; selection and the open chair are pruned against what actually
     * survived. Plans are small, so 80 snapshots × an O(tables+guests) merge is
     * milliseconds.
     */
    integrateStoredPlan: (stored) => {
      const { plan, baseDoc, past, future, selectedTableIds, activeSeat } = get()
      if (!plan) {
        get().loadPlan(stored)
        return
      }
      const base = baseDoc ?? docOf(stored)
      const theirs = docOf(stored)
      const merged = mergeDocs(base, docOf(plan), theirs)
      const rebase = (snapshot: Snapshot): Snapshot => mergeDocs(base, snapshot, theirs)

      const tableIds = new Set(merged.tables.map((table) => table.id))
      const seatIds = new Set(merged.tables.flatMap((t) => t.seats.map((seat) => seat.id)))
      set({
        plan: {
          ...plan,
          ...merged,
          version: stored.version,
          updatedAt: stored.updatedAt,
        },
        baseDoc: theirs,
        past: past.map(rebase),
        future: future.map(rebase),
        selectedTableIds: selectedTableIds.filter((id) => tableIds.has(id)),
        activeSeat: activeSeat && seatIds.has(activeSeat.seatId) ? activeSeat : null,
        // Dirty exactly when the merge kept something of ours storage has not
        // seen — the autosave subscription picks that up and writes it.
        saveStatus: docsEqual(merged, theirs) ? 'saved' : 'dirty',
        saveError: null,
      })
    },

    setSaveStatus: (status, error = null) => set({ saveStatus: status, saveError: error }),

    showToast: (message, tone = 'info') => set({ toast: { id: Date.now(), message, tone } }),
    dismissToast: () => set({ toast: null }),

    // --- history -----------------------------------------------------------

    beginTransaction: () => pushUndo(),

    transaction: (fn) => {
      pushUndo()
      inTransaction = true
      try {
        fn()
      } finally {
        inTransaction = false
      }
    },

    undo: () => {
      const { plan, past, future } = get()
      if (!plan || past.length === 0) return
      const previous = past[past.length - 1]
      set({
        plan: restore(previous, plan),
        past: past.slice(0, -1),
        future: [...future, snapshotOf(plan)],
        saveStatus: 'dirty',
      })
    },

    redo: () => {
      const { plan, past, future } = get()
      if (!plan || future.length === 0) return
      const next = future[future.length - 1]
      set({
        plan: restore(next, plan),
        past: [...past, snapshotOf(plan)],
        future: future.slice(0, -1),
        saveStatus: 'dirty',
      })
    },

    canUndo: () => get().past.length > 0,
    canRedo: () => get().future.length > 0,

    // --- selection ---------------------------------------------------------

    selectTables: (ids) => set({ selectedTableIds: ids, activeSeat: null }),

    toggleTableSelection: (id) =>
      set((state) => ({
        selectedTableIds: state.selectedTableIds.includes(id)
          ? state.selectedTableIds.filter((existing) => existing !== id)
          : [...state.selectedTableIds, id],
        activeSeat: null,
      })),

    clearSelection: () => set({ selectedTableIds: [], activeSeat: null }),
    setActiveSeat: (ref) => set({ activeSeat: ref, selectedTableIds: [] }),

    // --- plan meta ---------------------------------------------------------

    /**
     * Commit a new plan name, or do nothing at all.
     *
     * Blank is not a rename — it is a field mid-retype — so an empty commit
     * leaves the title exactly as it was rather than stamping a placeholder
     * over a name the couple chose. A commit that says what the title already
     * says is not a rename either, so blurring the field without touching it
     * costs neither an undo entry nor a save. The editing buffer lives in the
     * input (`PlanTitle`), which is why nothing here has to tolerate a
     * half-typed name.
     */
    renamePlan: (title) => {
      const next = title.trim()
      const current = get().plan
      if (!current || !next || next === current.title) return
      edit((plan) => void (plan.title = next))
    },

    setPlanDetails: (patch) =>
      edit((plan) => {
        if (patch.eventKind !== undefined) plan.eventKind = patch.eventKind
        if (patch.eventDate !== undefined) plan.eventDate = patch.eventDate
        if (patch.venue !== undefined) plan.venue = patch.venue
      }),

    // The camera is not part of the document history — it rides along on saves
    // so the view is restored on reload, but undo never moves it.
    setViewport: (viewport) => {
      const { plan } = get()
      if (!plan) return
      set({ plan: { ...plan, viewport } })
    },

    // --- plan settings -----------------------------------------------------
    //
    // These are what a *new* table is born with. Changing one never touches the
    // tables already in the room — that is what the explicit apply actions
    // below are for, because silently re-chairing a room someone has already
    // seated is not a preference, it is a surprise.

    setDefaultSeatCount: (shape, count) =>
      edit((plan) => {
        plan.settings.defaultSeats[shape] = clampSeatCount(count, shape)
      }),

    setDefaultTableColor: (color) =>
      edit((plan) => void (plan.settings.defaultTableColor = color)),

    setSeatLabels: (mode) => edit((plan) => void (plan.settings.seatLabels = mode)),

    applyDefaultSeatCount: (shape) => {
      const current = get().plan
      if (!current) return { tables: 0, unseated: 0 }
      const target = clampSeatCount(current.settings.defaultSeats[shape], shape)
      const affected = current.tables.filter(
        (table) => table.shape === shape && table.seatCount !== target,
      )
      if (affected.length === 0) return { tables: 0, unseated: 0 }

      let unseated = 0
      edit((plan) => {
        for (const table of plan.tables) {
          if (table.shape !== shape || table.seatCount === target) continue
          unseated += resizeSeats(table, target)
          // The table may have grown; keep it on the floor.
          const at = clampTableCenter(
            table.shape,
            table.seatCount,
            table.x,
            table.y,
            table.rotation,
            floorBounds(plan.tables, new Set([table.id])),
          )
          table.x = at.x
          table.y = at.y
        }
      })
      return { tables: affected.length, unseated }
    },

    applyDefaultTableColor: () => {
      const current = get().plan
      if (!current) return 0
      const color = current.settings.defaultTableColor
      const affected = current.tables.filter((table) => table.color !== color).length
      if (affected === 0) return 0
      edit((plan) => {
        for (const table of plan.tables) table.color = color
      })
      return affected
    },

    // --- tables ------------------------------------------------------------

    addTable: (shape, at) => {
      const id = newId()
      edit((plan) => {
        const seatCount = clampSeatCount(plan.settings.defaultSeats[shape], shape)
        const maxZ = topZ(plan)
        const where = clampTableCenter(
          shape,
          seatCount,
          at?.x ?? 0,
          at?.y ?? 0,
          0,
          floorBounds(plan.tables),
        )
        plan.tables.push({
          id,
          name: nextTableName(plan.tables, shape, plan.eventKind, plan.language),
          shape,
          color: plan.settings.defaultTableColor,
          x: where.x,
          y: where.y,
          rotation: 0,
          seatCount,
          zIndex: maxZ + 1,
          seats: makeSeats(seatCount),
        })
      })
      set({ selectedTableIds: [id], activeSeat: null })
      return id
    },

    deleteTables: (ids) => {
      if (ids.length === 0) return
      const removing = new Set(ids)
      edit((plan) => {
        plan.tables = plan.tables.filter((table) => !removing.has(table.id))
      })
      set({ selectedTableIds: [], activeSeat: null })
    },

    duplicateTable: (id) => {
      const source = get().plan?.tables.find((table) => table.id === id)
      if (!source) return null
      const copyId = newId()
      edit((plan) => {
        const maxZ = topZ(plan)
        const where = clampTableCenter(
          source.shape,
          source.seatCount,
          source.x + 70,
          source.y + 70,
          source.rotation,
          floorBounds(plan.tables),
        )
        plan.tables.push({
          ...clone(source),
          id: copyId,
          name: nextTableName(plan.tables, source.shape, plan.eventKind, plan.language),
          x: where.x,
          y: where.y,
          zIndex: maxZ + 1,
          // Chairs are copied empty: a guest can only ever sit in one chair.
          seats: makeSeats(source.seatCount),
        })
      })
      set({ selectedTableIds: [copyId], activeSeat: null })
      return copyId
    },

    nudgeTables: (ids, dx, dy, options) => {
      const moving = new Set(ids)
      edit(
        (plan) => {
          const bounds = floorBounds(plan.tables, moving)
          for (const table of plan.tables) {
            if (!moving.has(table.id)) continue
            const at = confine(table, table.x + dx, table.y + dy, bounds)
            table.x = at.x
            table.y = at.y
          }
        },
        { history: options?.transient ? false : true },
      )
    },

    /**
     * A drag or rotation has just ended: if the moved tables came to rest on
     * another table — tops crossing, or a chair standing on someone's top —
     * translate them, as one rigid piece, to the nearest clean spot. The tables
     * that were not part of the gesture never move, so a plan that has always
     * held an overlap only heals when one of its tables is next picked up.
     * `history: false` folds the nudge into the gesture's open transaction:
     * one undo reverses drag and nudge together.
     */
    settleTables: (ids) => {
      const current = get().plan
      if (!current) return
      const moving = new Set(ids)
      const moved = current.tables.filter((table) => moving.has(table.id))
      const others = current.tables.filter((table) => !moving.has(table.id))
      if (moved.length === 0 || others.length === 0) return
      const shift = separateFrom(moved, others)
      if (!shift || (shift.dx === 0 && shift.dy === 0)) return
      edit(
        (plan) => {
          const bounds = floorBounds(plan.tables, moving)
          for (const table of plan.tables) {
            if (!moving.has(table.id)) continue
            const at = confine(table, table.x + shift.dx, table.y + shift.dy, bounds)
            table.x = at.x
            table.y = at.y
          }
        },
        { history: false },
      )
    },

    moveTables: (targets) => {
      const plan = get().plan
      if (!plan) return
      const byId = new Map(targets.map((target) => [target.id, target]))
      const bounds = floorBounds(plan.tables, new Set(byId.keys()))
      patchTables((table) => {
        const target = byId.get(table.id)
        return target ? confine(table, target.x, target.y, bounds) : null
      })
    },

    setTablePosition: (id, x, y) => {
      const plan = get().plan
      if (!plan) return
      const bounds = floorBounds(plan.tables, new Set([id]))
      patchTables((table) => (table.id === id ? confine(table, x, y, bounds) : null))
    },

    setTableRotation: (id, rotation) =>
      patchTables((table) =>
        table.id === id ? { rotation: ((rotation % 360) + 360) % 360 } : null,
      ),

    /**
     * Align and distribute, over the selection only.
     *
     * `lib/arrange.ts` does the arithmetic on boxes; all that is left here is
     * turning tables into boxes and the answer back into a move. Both are one
     * `edit()`, so a row of nine tables is one press of ⌘Z, and both go through
     * `confine` so a table can never be aligned off the floor.
     *
     * Neither settles afterwards. Settling nudges the whole moved group until
     * nothing overlaps, which would slide the tables straight back off the line
     * they were just put on — so an alignment that stacks two tables that were
     * already stacked on the free axis leaves them stacked, flagged in red on
     * the canvas and listed by the export dialog, exactly like any other
     * overlap the room was already carrying.
     */
    alignTables: (ids, edge) => arrangeSelection(ids, (boxes) => alignBoxes(boxes, edge)),

    distributeTables: (ids, along) =>
      arrangeSelection(ids, (boxes) => distributeBoxes(boxes, along)),

    /**
     * One chair count across a mixed selection.
     *
     * The cap is per shape, so a round table and a banquet table asked for 20
     * do not end up the same: the round one stops at 13 where its chairs would
     * start crowding, the banquet one simply gets longer. Returns how many
     * tables changed and how many guests it had to unseat, so the caller can
     * warn *before* it happens rather than apologise after.
     */
    setSeatCountFor: (ids, count) => {
      const current = get().plan
      if (!current) return { tables: 0, unseated: 0 }
      const chosen = new Set(ids)
      const affected = current.tables.filter(
        (table) => chosen.has(table.id) && table.seatCount !== clampSeatCount(count, table.shape),
      )
      if (affected.length === 0) return { tables: 0, unseated: 0 }

      let unseated = 0
      edit((plan) => {
        for (const table of plan.tables) {
          if (!chosen.has(table.id)) continue
          const target = clampSeatCount(count, table.shape)
          if (table.seatCount === target) continue
          unseated += resizeSeats(table, target)
          // A banquet table grows as it gains chairs; keep it on the floor.
          const at = clampTableCenter(
            table.shape,
            table.seatCount,
            table.x,
            table.y,
            table.rotation,
            floorBounds(plan.tables, new Set([table.id])),
          )
          table.x = at.x
          table.y = at.y
        }
      })
      return { tables: affected.length, unseated }
    },

    /**
     * One angle across the selection, then settle, as a rotate gesture does.
     * `transient` is the slider's per-frame path: the caller opens its own
     * transaction on pointer-down, so a drag from 0° to 90° is one undo entry
     * rather than ninety, and the settle waits for the pointer to come up.
     */
    setRotationFor: (ids, rotation, options) => {
      const current = get().plan
      if (!current) return
      const chosen = new Set(ids)
      const angle = ((rotation % 360) + 360) % 360
      if (!current.tables.some((table) => chosen.has(table.id) && table.rotation !== angle)) return
      edit(
        (plan) => {
          for (const table of plan.tables) {
            if (chosen.has(table.id)) table.rotation = angle
          }
        },
        { history: options?.transient ? false : true },
      )
      if (!options?.transient) get().settleTables(ids)
    },

    /** Same contract as `renamePlan`: blank or unchanged is not a rename. */
    renameTable: (id, name) => {
      const next = name.trim().slice(0, 80)
      const table = get().plan?.tables.find((t) => t.id === id)
      if (!table || !next || next === table.name) return
      edit((plan) => {
        const entity = plan.tables.find((t) => t.id === id)
        if (entity) entity.name = next
      })
    },

    setTableColor: (id, color) =>
      edit((plan) => {
        const table = plan.tables.find((t) => t.id === id)
        if (table) table.color = color
      }),

    bringToFront: (id) =>
      edit(
        (plan) => {
          const table = plan.tables.find((t) => t.id === id)
          if (!table) return
          const maxZ = topZ(plan)
          if (table.zIndex !== maxZ) table.zIndex = maxZ + 1
        },
        { history: false },
      ),

    /**
     * Turns the proposals from a photographed chart into real tables.
     *
     * Ids and chairs are minted here rather than by the detector, so an
     * import is an ordinary edit: one undo step, saved by the same autosave,
     * with no new write path to keep honest. `replace` is offered because the
     * usual case is a fresh plan still holding the starter template, and the
     * planner wants their venue's room, not their venue's room *plus* five
     * sample tables floating over it.
     */
    importDetectedTables: (tables, mode) => {
      if (tables.length === 0) return 0
      const ids = tables.map(() => newId())
      edit((plan) => {
        const base = mode === 'replace' ? 0 : topZ(plan, tables.length)
        // The detector works in its own origin, so a photographed room can land
        // anywhere. Centre it on the room before clamping: shifting the whole
        // set keeps the venue's proportions, where clamping table by table on a
        // badly-placed room would pile them against one wall.
        const detectedBounds = unionBounds(
          tables.map((detected) =>
            tableBounds(
              detected.shape,
              clampSeatCount(detected.seatCount, detected.shape),
              detected.x,
              detected.y,
              detected.rotation,
            ),
          ),
        )
        const shiftX = detectedBounds
          ? -(detectedBounds.minX + detectedBounds.maxX) / 2
          : 0
        const shiftY = detectedBounds
          ? -(detectedBounds.minY + detectedBounds.maxY) / 2
          : 0

        // No per-table clamp after centring. A centred venue that fits is
        // already inside the room, and one that is *bigger* than the room must
        // not be crushed into it — clamping stacked every overflowing table
        // onto the wall in overlapping piles, destroying the collision-free
        // geometry the detector guarantees. An oversized venue overhangs the
        // drawn room instead, exactly like a grandfathered table: it may not be
        // dragged further out, and the camera roam already reaches it.
        const imported: Table[] = tables.map((detected, index) => {
          const seatCount = clampSeatCount(detected.seatCount, detected.shape)
          const rotation = ((detected.rotation % 360) + 360) % 360
          return {
            id: ids[index],
            name:
              localTableName(detected.name.slice(0, 80), plan.language) ||
              planWords(plan.language).table(index + 1),
            shape: detected.shape,
            color: detected.color,
            x: detected.x + shiftX,
            y: detected.y + shiftY,
            rotation,
            seatCount,
            zIndex: base + index + 1,
            seats: makeSeats(seatCount),
          }
        })
        if (mode === 'replace') {
          // Guests survive a replace — they are people, not furniture — but every
          // seating assignment goes with the chairs it referred to.
          plan.tables = imported
        } else {
          plan.tables.push(...imported)
        }
      })
      set({ selectedTableIds: [], activeSeat: null })
      return tables.length
    },

    // --- chairs ------------------------------------------------------------

    // The store never toasts here: it has no access to the catalogue, and an
    // English sentence in a Russian editor is worse than silence. Callers that
    // are not already disabled at the bound (the hotkeys) toast on `false`.
    addSeat: (tableId) => {
      const table = get().plan?.tables.find((t) => t.id === tableId)
      if (!table) return false
      if (table.seatCount >= maxSeatCount(table.shape)) return false
      edit((plan) => {
        const target = plan.tables.find((t) => t.id === tableId)
        if (!target) return
        resizeSeats(target, target.seats.length + 1)
        const at = clampTableCenter(
          target.shape,
          target.seatCount,
          target.x,
          target.y,
          target.rotation,
          floorBounds(plan.tables, new Set([target.id])),
        )
        target.x = at.x
        target.y = at.y
      })
      return true
    },

    /**
     * Removes one chair. Without a seatId it drops a *trailing empty* chair in
     * preference to an occupied one, so clicking "−" never silently unseats a
     * guest while empty chairs are still available.
     */
    removeSeat: (tableId, seatId) => {
      const table = get().plan?.tables.find((t) => t.id === tableId)
      if (!table) return false
      if (table.seatCount <= MIN_SEATS) return false

      let targetId = seatId
      if (!targetId) {
        const lastEmpty = [...table.seats].reverse().find((seat) => !seat.guestId)
        targetId = (lastEmpty ?? table.seats[table.seats.length - 1]).id
      }

      edit((plan) => {
        const target = plan.tables.find((t) => t.id === tableId)
        if (!target) return
        target.seats = reindex(target.seats.filter((seat) => seat.id !== targetId))
        target.seatCount = target.seats.length
      })

      const active = get().activeSeat
      if (active?.seatId === targetId) set({ activeSeat: null })
      return true
    },

    setSeatCount: (tableId, count) => {
      const table = get().plan?.tables.find((t) => t.id === tableId)
      if (!table) return
      const target = clampSeatCount(count, table.shape)
      if (target === table.seatCount) return

      edit((plan) => {
        const entity = plan.tables.find((t) => t.id === tableId)
        if (!entity) return
        resizeSeats(entity, target)
        // A banquet table gets *longer* as chairs are added, so growing one can
        // push it through a wall.
        const at = clampTableCenter(
          entity.shape,
          entity.seatCount,
          entity.x,
          entity.y,
          entity.rotation,
          floorBounds(plan.tables, new Set([entity.id])),
        )
        entity.x = at.x
        entity.y = at.y
      })
    },

    // --- guests ------------------------------------------------------------

    addGuest: (fullName, patch) => {
      const id = newId()
      edit((plan) => {
        plan.guests.push({
          id,
          fullName: fullName.trim().slice(0, 120) || 'Guest',
          groupLabel: patch?.groupLabel ?? null,
          note: patch?.note ?? null,
          rsvp: (patch?.rsvp as Rsvp) ?? 'pending',
          photoAssetId: patch?.photoAssetId ?? null,
          photoUrl: patch?.photoUrl ?? null,
        })
      })
      return id
    },

    updateGuest: (id, patch) =>
      edit((plan) => {
        const guest = plan.guests.find((g) => g.id === id)
        if (guest) Object.assign(guest, patch)
      }),

    deleteGuest: (id) => {
      edit((plan) => {
        plan.guests = plan.guests.filter((guest) => guest.id !== id)
        for (const table of plan.tables) {
          for (const seat of table.seats) {
            if (seat.guestId === id) seat.guestId = null
          }
        }
      })
      set({ activeSeat: null })
    },

    /** Accepts one name per line, or "Name, Group" per line. */
    importGuests: (raw) => {
      const rows = raw
        .split(/[\n\r]+/)
        .map((line) => line.trim())
        .filter(Boolean)
      // Parse before editing, and report what was actually added: a line like
      // "," splits to an empty name and must be neither imported nor counted —
      // and a paste of nothing valid must not land an empty undo entry.
      const parsed = rows
        .map((row) => row.split(/\s*[,;\t]\s*/))
        .filter(([name]) => Boolean(name))
      if (parsed.length === 0) return 0

      edit((plan) => {
        for (const [name, group] of parsed) {
          plan.guests.push({
            id: newId(),
            fullName: name.slice(0, 120),
            groupLabel: group ? group.slice(0, 60) : null,
            note: null,
            rsvp: 'pending',
            photoAssetId: null,
            photoUrl: null,
          })
        }
      })
      return parsed.length
    },

    /** Seats a guest, vacating whatever chair they previously occupied. */
    assignGuest: (guestId, ref) =>
      edit((plan) => {
        for (const table of plan.tables) {
          for (const seat of table.seats) {
            if (seat.guestId === guestId) seat.guestId = null
          }
        }
        const table = plan.tables.find((t) => t.id === ref.tableId)
        const seat = table?.seats.find((s) => s.id === ref.seatId)
        if (seat) seat.guestId = guestId
      }),

    seatGuest: (guestId, target) => {
      const plan = get().plan
      if (!plan) return
      const occupant =
        plan.tables.find((t) => t.id === target.tableId)?.seats.find((s) => s.id === target.seatId)
          ?.guestId ?? null
      if (occupant === guestId) return
      const from = guestSeatIndex(plan).get(guestId)
      if (occupant && from) {
        // "These two should switch places": one transaction, so undoing a swap
        // restores both guests and never strands one of them in the roster.
        get().transaction(() => {
          get().assignGuest(guestId, target)
          get().assignGuest(occupant, { tableId: from.table.id, seatId: from.seat.id })
        })
      } else {
        get().assignGuest(guestId, target)
      }
    },

    seatGuestAtTable: (guestId, tableId) => {
      const table = get().plan?.tables.find((t) => t.id === tableId)
      if (!table) return null
      // Someone already at this table gets moved too: a seated guest only
      // arrives here by asking to change chairs.
      const free = [...table.seats]
        .sort((a, b) => a.position - b.position)
        .find((s) => !s.guestId)
      if (!free) return null
      const target = { tableId, seatId: free.id }
      get().assignGuest(guestId, target)
      return target
    },

    clearSeat: (ref) =>
      edit((plan) => {
        const table = plan.tables.find((t) => t.id === ref.tableId)
        const seat = table?.seats.find((s) => s.id === ref.seatId)
        if (seat) seat.guestId = null
      }),

    /**
     * Empty whole tables, returning everyone sitting at them to the roster.
     * The chairs stay — this clears people, not furniture — and the guests stay
     * in the plan, so the only thing that changes is where they are sitting.
     *
     * Counting first means an already-empty selection costs neither an undo
     * entry nor a save, and the count is what the button and the toast say out
     * loud so nobody has to guess how much one click is about to move.
     */
    unseatTables: (ids) => {
      const current = get().plan
      if (!current) return 0
      const chosen = new Set(ids)
      let freed = 0
      for (const table of current.tables) {
        if (!chosen.has(table.id)) continue
        freed += table.seats.filter((seat) => seat.guestId).length
      }
      if (freed === 0) return 0
      edit((plan) => {
        for (const table of plan.tables) {
          if (!chosen.has(table.id)) continue
          for (const seat of table.seats) seat.guestId = null
        }
      })
      return freed
    },

    /**
     * One action for "move this table over there" and "these two tables trade":
     * both sides are lifted, then set down at the other table. The chairs stay
     * where geometry puts them and keep their ids, so an open chair panel still
     * points somewhere real. Counting first means two empty tables cost neither
     * an undo entry nor a save.
     */
    swapTableGuests: (aId, bId) => {
      const current = get().plan
      if (!current || aId === bId) return null
      const seatedAt = (table: Table | undefined) =>
        table
          ? table.seats.filter((seat) => seat.guestId).sort((x, y) => x.position - y.position)
          : null
      const fromA = seatedAt(current.tables.find((t) => t.id === aId))
      const fromB = seatedAt(current.tables.find((t) => t.id === bId))
      if (!fromA || !fromB) return null
      const result: TableSwap = { toB: fromA.length, toA: fromB.length, unseated: 0 }
      if (fromA.length === 0 && fromB.length === 0) return result

      edit((plan) => {
        const a = plan.tables.find((t) => t.id === aId)
        const b = plan.tables.find((t) => t.id === bId)
        if (!a || !b) return
        for (const seat of [...a.seats, ...b.seats]) seat.guestId = null
        const sizes = new Map([a, b].map((table) => [table.id, table.seatCount]))
        const lostAtB = seatArrivals(b, fromA)
        const lostAtA = seatArrivals(a, fromB)
        result.toB -= lostAtB
        result.toA -= lostAtA
        result.unseated = lostAtB + lostAtA
        // Chairs added to a banquet table lengthen it, which can reach a wall.
        for (const table of [a, b]) {
          if (table.seatCount === sizes.get(table.id)) continue
          const at = clampTableCenter(
            table.shape,
            table.seatCount,
            table.x,
            table.y,
            table.rotation,
            floorBounds(plan.tables, new Set([table.id])),
          )
          table.x = at.x
          table.y = at.y
        }
      })
      return result
    },

    seatNewGuest: (ref, fullName) => {
      const id = newId()
      edit((plan) => {
        plan.guests.push({
          id,
          fullName: fullName.trim().slice(0, 120) || 'Guest',
          groupLabel: null,
          note: null,
          rsvp: 'pending',
          photoAssetId: null,
          photoUrl: null,
        })
        const table = plan.tables.find((t) => t.id === ref.tableId)
        const seat = table?.seats.find((s) => s.id === ref.seatId)
        if (seat) seat.guestId = id
      })
      return id
    },

    /**
     * Fills empty chairs with unseated guests, keeping groups together: guests
     * are ordered by group first, so a family lands on one table rather than
     * being scattered across the room.
     */
    autoSeat: () => {
      const plan = get().plan
      if (!plan) return 0

      const seated = new Set(
        plan.tables.flatMap((table) => table.seats.map((seat) => seat.guestId).filter(Boolean)),
      )
      // Someone who cannot come needs no chair: a decline is never dealt one,
      // however many chairs stand empty.
      const waiting = plan.guests
        .filter((guest) => guest.rsvp !== 'no' && !seated.has(guest.id))
        .sort((a, b) => {
          const groupA = a.groupLabel ?? '￿'
          const groupB = b.groupLabel ?? '￿'
          if (groupA !== groupB) return groupA.localeCompare(groupB)
          return a.fullName.localeCompare(b.fullName)
        })
      if (waiting.length === 0) return 0

      let placed = 0
      edit((draft) => {
        const queue = [...waiting]
        // Sweetheart tables are reserved for the couple — never auto-filled.
        const tables = draft.tables
          .filter((table) => table.shape !== 'sweetheart')
          .sort((a, b) => a.y - b.y || a.x - b.x)
        for (const table of tables) {
          for (const seat of table.seats) {
            if (seat.guestId || queue.length === 0) continue
            const guest = queue.shift()
            if (!guest) break
            seat.guestId = guest.id
            placed += 1
          }
        }
      })
      return placed
    },
  }
})

// --- selectors --------------------------------------------------------------

export function seatedGuestIds(plan: Plan | null): Set<string> {
  const ids = new Set<string>()
  if (!plan) return ids
  for (const table of plan.tables) {
    for (const seat of table.seats) {
      if (seat.guestId) ids.add(seat.guestId)
    }
  }
  return ids
}

export function guestSeatIndex(plan: Plan | null): Map<string, { table: Table; seat: Seat }> {
  const index = new Map<string, { table: Table; seat: Seat }>()
  if (!plan) return index
  for (const table of plan.tables) {
    for (const seat of table.seats) {
      if (seat.guestId) index.set(seat.guestId, { table, seat })
    }
  }
  return index
}
